import React from 'react';
import { CreditReason, reasonOptions } from '../../lib/creditReasonApi';

/**
 * D44 — the reason a credit note (and, from VR-2, a vendor debit note) is given:
 * one choice from the shared list, plus a note that "Other" requires.
 *
 * Controlled and stateless: the page owns the values and saves them. The list
 * must be loaded WITH hidden reasons, so a note filed under a reason an admin has
 * since hidden still shows it (`reasonOptions`).
 */
export const CreditReasonField: React.FC<{
  reasons: CreditReason[];
  reasonKey: string;
  reasonNote: string;
  onChange: (next: { reasonKey: string; reasonNote: string }) => void;
  /** The key the document was saved with, so a hidden one stays selectable. */
  savedKey?: string;
  disabled?: boolean;
  /** Shown under the field, e.g. that the reason is not printed on the client's copy. */
  hint?: string;
}> = ({ reasons, reasonKey, reasonNote, onChange, savedKey, disabled, hint }) => {
  const options = reasonOptions(reasons, savedKey || reasonKey);
  const chosen = options.find((o) => o.value === reasonKey);
  const noteRequired = Boolean(chosen?.noteRequired);
  const noteMissing = noteRequired && !reasonNote.trim();
  const inputClass = 'w-full h-8 rounded-md border border-slate-200 px-2 text-xs focus:border-primary-500';

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <div>
        <label htmlFor="credit-reason" className="block text-xs font-medium text-slate-600 mb-1">
          Reason <span className="text-red-600">*</span>
        </label>
        <select
          id="credit-reason"
          className={inputClass}
          value={reasonKey}
          disabled={disabled}
          onChange={(e) => onChange({ reasonKey: e.target.value, reasonNote })}
        >
          <option value="">Choose a reason</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      <div className="md:col-span-2">
        <label htmlFor="credit-reason-note" className="block text-xs font-medium text-slate-600 mb-1">
          Reason note {noteRequired && <span className="text-red-600">*</span>}
        </label>
        <textarea
          id="credit-reason-note"
          className={`w-full min-h-[32px] rounded-md border px-2 py-1.5 text-xs focus:border-primary-500 resize-y ${
            noteMissing ? 'border-amber-400' : 'border-slate-200'
          }`}
          rows={1}
          maxLength={1000}
          value={reasonNote}
          disabled={disabled}
          placeholder={noteRequired ? 'Required for "Other": say what happened' : 'Optional detail'}
          onChange={(e) => onChange({ reasonKey, reasonNote: e.target.value })}
        />
      </div>
      {hint && <div className="md:col-span-3 text-xs text-slate-500">{hint}</div>}
    </div>
  );
};

export default CreditReasonField;
